import {store} from '../../store/global-store';
import _get from 'lodash/get';
import _u from '../../shared/utils';

class InputMeta {
    constructor(type, params) {
        this.type = type;
        this.label = params.label;
        this.options = params.options;
        this._filterKey = params.filterKey;
    }

    get id() {
        return this._filterKey;
    }

    get value() {
        return this.filterValue;
    }

    set value(newValue) {
        this.applyFilter({key: this._filterKey, value: newValue});
    }

    get filterKey() {
        return this._filterKey;
    }

    get filterValue() {
        return this._getFilterValue(this._filterKey);
    }

    _getFilterValue(filterKey) {
        return _get(store.getters.filter, filterKey);
    }

    isValueSet() {
        return this.value != null;
    }

    applyFilter(keyValue) {
        store.dispatch('applyFilter', keyValue);
    }

    getSummaryLabel(noValueLabel, withPrefix) {
        let summaryLabel = this._getSummaryLabel() || noValueLabel;

        if (summaryLabel && withPrefix) {
            summaryLabel = `${this.label}: ${summaryLabel}`;
        }

        return summaryLabel || this.label;
    }

    getTags() {
        return [
            {
                label: this.getSummaryLabel('', true),
                value: this.value,
                input: this,
            },
        ];
    }

    clear() {
    // overriden in inherited classes
    }

    _getSummaryLabel() {
        return '';
    }
}

class CheckboxInputMeta extends InputMeta {
    constructor(params) {
        super('check-input', params);
    }

    isValueSet() {
        return this.value;
    }

    clear() {
        this.value = false;
    }

    _getSummaryLabel() {
        return this.value ? this.value.label : null;
    }
}

class SelectInputMeta extends InputMeta {
    constructor(params) {
        super('select-input', params);
        this._hasPrefix = params.hasPrefix ?? true;
    }

    get value() {
        const selectedValue = this.filterValue;
        return this.options.find((option) => option.value === selectedValue);
    }

    set value(selectedOption) {
        this.applyFilter({key: this._filterKey, value: selectedOption ? selectedOption.value : null});
    }

    isValueSet() {
        return this.value && this.value.value != null;
    }

    clear() {
        this.value = null;
    }

    _getSummaryLabel() {
        return this.value ? this.value.label : null;
    }

    getTags() {
        return [
            {
                label: this.getSummaryLabel('', this._hasPrefix),
                value: this.value,
                input: this,
            },
        ];
    }
}

class NoInputMeta extends InputMeta {
    constructor(params) {
        super('filter-without-input', params);
    }

    clear() {
        this.value = null;
    }
}

class MultiselectInputMeta extends InputMeta {
    constructor(params) {
        super('multiselect-input', params);
    }

    get value() {
        const selectedValues = this.filterValue;

        if (!selectedValues) {
            return [];
        }

        return this.options.filter((option) => selectedValues.includes(option.value));
    }

    set value(selectedOptions) {
        const selectedValues = selectedOptions.map((option) => option.value);
        this.applyFilter({key: this._filterKey, value: selectedValues});
    }

    isValueSet() {
        return this.value && this.value.length > 0;
    }

    clear(option) {
        if (option) {
            this.value = this.value.filter((v) => v.value !== option.value);
        } else {
            this.value = [];
        }
    }

    getTags() {
        return this.value.map((option) => {
            return {
                label: option.label,
                value: option,
                input: this,
            };
        });
    }

    _getSummaryLabel() {
        return this.value && this.value.length > 0 ? this.value.map((v) => v.label).join(', ') : null;
    }
}

class RangeInputMeta extends InputMeta {
    constructor(params) {
        super('range-input', params);
        this._filterKey = params.filterKey;
        this._filterMinKey = params.filterMinKey;
        this._filterMaxKey = params.filterMaxKey;
        this._toLabel = params.toLabel;
        this._fromLabel = params.fromLabel;
        this.formatter = params.formatter;
    }

    get id() {
        return this._filterKey ? this._filterKey : this._filterMinKey;
    }

    get value() {
        const minSelected = this.options.find((option) => option.value === this.minValue);
        const maxSelected = this.options.find((option) => option.value === this.maxValue);
        return {min: minSelected, max: maxSelected};
    }

    set value(newValue) {
        const min = newValue && newValue.min ? newValue.min.value : null;
        const max = newValue && newValue.max ? newValue.max.value : null;
        this.applyFilter({key: this._filterMinKey, value: min});
        this.applyFilter({key: this._filterMaxKey, value: max});
    }

    get minValue() {
        return this._getFilterValue(this._filterMinKey);
    }

    get maxValue() {
        return this._getFilterValue(this._filterMaxKey);
    }

    get filterMinKey() {
        return this._filterMinKey;
    }

    get filterMaxKey() {
        return this._filterMaxKey;
    }

    isValueSet() {
        return this.minValue != null || this.maxValue != null;
    }

    clear() {
        this.value = null;
    }

    _formatRange(min, max) {
        let result = null;

        if (min && max) {
            if (min === max) {
                result = `${min.label}`;
            } else if (!min.value) {
                result = `${this._toLabel} ${max.label}`;
            } else if (!max.value) {
                result = `${this._fromLabel} ${min.label}`;
            } else {
                result = `${min.label} - ${max.label}`;
            }
        } else if (min) {
            result = min.label.includes('+') ? min.label : `${min.label}+`;
        } else if (max) {
            result = !max.value ? max.label : `${this._fromLabel} ${max.label}`;
        }

        if (this.formatter) {
            result = this.formatter(result);
        }

        return result;
    }

    _getSummaryLabel() {
        return this._formatRange(this.value.min, this.value.max);
    }
}

class TwoSidesRangeInputMeta extends InputMeta {
    constructor(params) {
        super('range-input', params);
        this._filterKey = params.filterKey;
        this._filterMinKey = params.filterMinKey;
        this._filterMaxKey = params.filterMaxKey;
        this._leftSideLabel = params.leftSideLabel;
        this._rightSideLabel = params.rightSideLabel;
    }

    get minValue() {
        return this._getFilterValue(this._filterMinKey);
    }

    get maxValue() {
        return this._getFilterValue(this._filterMaxKey);
    }

    get value() {
        return {min: this.minValue, max: this.maxValue};
    }

    set value(newValue) {
        if (!newValue) {
            this.applyFilter({key: this._filterKey, value: null});
            return;
        }

        const min = newValue && newValue.min ? newValue.min.value : null;
        const max = newValue && newValue.max ? newValue.max.value : null;
        this.applyFilter({key: this._filterMinKey, value: min});
        this.applyFilter({key: this._filterMaxKey, value: max});
    }

    isValueSet() {
        return this.minValue != null || this.maxValue != null;
    }

    clear() {
        this.value = null;
    }

    _getSummaryLabel() {
        if (this.minValue) {
            return this._rightSideLabel;
        } else {
            return this._leftSideLabel;
        }
    }

    getTags() {
        return [
            {
                label: this.getSummaryLabel('', false),
                value: this.value,
                input: this,
            },
        ];
    }
}

class CheckGroupInputMeta extends InputMeta {
    constructor(params) {
        super('check-group-input', params);
    }

    get value() {
        return this.filterValue;
    }

    set value(newValue) {
        this.applyFilter({key: this._filterKey, value: newValue});
    }

    isValueSet() {
        return this.value && Object.values(this.value).find((v) => v);
    }

    getTags() {
        return Object.keys(this.value)
            .filter((key) => this.value[key])
            .map((key) => {
                return {
                    label: this.options[key],
                    value: key,
                    input: this,
                };
            });
    }

    clear(key) {
        if (key) {
            this.value[key] = false;
        } else {
            Object.keys(this.value).map((k) => this.value[k] = false);
        }
    }

    _getSummaryLabel() {
        const selected = Object.keys(this.value).filter((key) => this.value[key]);
        return selected.length > 0 ? `(${selected.length})` : null;
    }
}

class AddressInputMeta extends InputMeta {
    constructor(params) {
        super('address-input', params);
        this.error = params.error;
    }

    get value() {
        return this.filterValue;
    }

    set value(newValue) {
    // do nothing, filter will be set in autocomplete callback
    }

    isValueSet() {
        return false;
    }
}

const dynamicMetaMapping = {
    'Input': InputMeta,
    'Checkbox': CheckboxInputMeta,
    'Dropdown': SelectInputMeta,
    'Range': RangeInputMeta,
};

const DynamicInputMetaFactory = {
    createInputMeta(uiControl, params) {
        const MetaClass = dynamicMetaMapping[uiControl];

        if (!MetaClass) {
            console.error(`Unsupported uiControl '${uiControl}'. Unable to create input meta.`);
            return;
        }

        if (MetaClass == RangeInputMeta) {
            const propertyName = _u.capitalizeFirstLetter(params.filterKey);
            params.filterMinKey = `${params.filterKeyPrefix}.min${propertyName}`;
            params.filterMaxKey = `${params.filterKeyPrefix}.max${propertyName}`;
        } else if (params.filterKeyPrefix) {
            params.filterKey = `${params.filterKeyPrefix}.${params.filterKey}`;
        }

        return new MetaClass(params);
    },
};


export {AddressInputMeta, SelectInputMeta, MultiselectInputMeta, CheckGroupInputMeta, RangeInputMeta,
    InputMeta, CheckboxInputMeta, DynamicInputMetaFactory, NoInputMeta, TwoSidesRangeInputMeta};

